import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Aprendices";
const MODULO_TEST_KOLB = "TestdeKolb";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListarAprendices = () => {
  const [aprendices, setAprendices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModulo = (nombreModulo) => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(nombreModulo);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloAprendices = useMemo(() => getModulo(MODULO), []);
  const moduloTestKolb = useMemo(() => getModulo(MODULO_TEST_KOLB), []);

  const puedeCrear =
    moduloAprendices?.puedeCrear === true ||
    moduloAprendices?.crear === true ||
    moduloAprendices?.create === true;

  const puedeActualizar =
    moduloAprendices?.puedeActualizar === true ||
    moduloAprendices?.actualizar === true ||
    moduloAprendices?.editar === true ||
    moduloAprendices?.update === true;

  const puedeEliminar =
    moduloAprendices?.puedeEliminar === true ||
    moduloAprendices?.eliminar === true ||
    moduloAprendices?.delete === true;

  const puedeCrearTestKolb =
    moduloTestKolb?.puedeCrear === true ||
    moduloTestKolb?.crear === true ||
    moduloTestKolb?.create === true;

  const puedeActualizarTestKolb =
    moduloTestKolb?.puedeActualizar === true ||
    moduloTestKolb?.actualizar === true ||
    moduloTestKolb?.editar === true ||
    moduloTestKolb?.update === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const descargarDocx = useCallback(
    async (url, nombreArchivo) => {
      try {
        const response = await axios.get(url, {
          ...authConfig(),
          responseType: "blob",
        });

        const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");

        link.href = urlBlob;
        link.setAttribute("download", nombreArchivo);

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(urlBlob);
      } catch (error) {
        console.error("Error al descargar el documento:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire("Error", "No se pudo generar el documento.", "error");
      }
    },
    [manejarErrorSesion]
  );

  const deleteAprendiz = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar aprendices.",
          "warning"
        );
        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(id);

        const { data } = await axios.delete(`/api/aprendices/${id}`, authConfig());

        setAprendices((prev) =>
          prev.filter((aprendiz) => aprendiz._id !== id)
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Aprendiz eliminado",
          showConfirmButton: false,
          timer: 1500,
        });

        return true;
      } catch (error) {
        console.error("Error al eliminar el aprendiz:", error);

        if (manejarErrorSesion(error)) return false;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar el aprendiz.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/aprendices/listall/", authConfig());

      if (!data || !Array.isArray(data.aprendices)) {
        throw new Error("Datos de aprendices no válidos.");
      }

      const aprendicesList = [...data.aprendices].sort((a, b) => {
        const ta = a?.createdAt
          ? new Date(a.createdAt).getTime()
          : parseInt(String(a._id).slice(0, 8), 16) * 1000;

        const tb = b?.createdAt
          ? new Date(b.createdAt).getTime()
          : parseInt(String(b._id).slice(0, 8), 16) * 1000;

        return tb - ta;
      });

      const ids = aprendicesList.map((aprendiz) => aprendiz._id);

      let kolbMap = {};

      try {
        const resTests = await axios.post(
          "/api/testkolb/existencias",
          { ids },
          authConfig()
        );

        if (resTests.data?.ok && resTests.data?.kolbMap) {
          kolbMap = resTests.data.kolbMap;
        }
      } catch (err) {
        console.error(
          "Error al obtener los testKolb masivamente:",
          err.message
        );
      }

      const aprendicesConKolb = aprendicesList.map((aprendiz) => ({
        ...aprendiz,
        testKolbId: kolbMap[aprendiz._id] || null,
      }));

      setAprendices(aprendicesConKolb);
    } catch (error) {
      console.error("Error al cargar los aprendices:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudieron cargar los aprendices.", "error");
      setAprendices([]);
    } finally {
      setLoading(false);
    }
  }, [manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData]);

  const dataTabla = useMemo(
    () =>
      aprendices.map((aprendiz) => ({
        ...aprendiz,

        imagen: (
          <div className="d-flex justify-content-end justify-content-md-center align-items-center w-100">
            {aprendiz.imgAprendiz ? (
              <img
                src={aprendiz.imgAprendiz}
                alt="Aprendiz"
                className="rounded-circle border border-2"
                style={{
                  width: 46,
                  height: 46,
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                className="rounded-circle border border-2 d-flex justify-content-center align-items-center text-secondary bg-body-tertiary"
                style={{
                  width: 46,
                  height: 46,
                }}
                title="Sin foto"
              >
                <i className="fa-solid fa-user"></i>
              </div>
            )}
          </div>
        ),

        funciones: (
          <div className="row g-2 align-items-center justify-content-end">
            <div className="col-12 col-md-auto order-0 order-md-0">
              <select
                className="form-select form-select-sm w-100"
                style={{
                  minWidth: 190,
                  height: 34,
                }}
                defaultValue=""
                onChange={(e) => {
                  const modelo = e.target.value;

                  if (modelo) {
                    descargarDocx(
                      `/api/hojavida/generar-docx/${aprendiz._id}?modelohv=${modelo}`,
                      `hoja_vida_${modelo}_${aprendiz.documentoAprendiz}.docx`
                    );

                    e.target.value = "";
                  }
                }}
                title="Selecciona Hoja de Vida"
              >
                <option value="" disabled>
                  Hoja de Vida…
                </option>
                <option value="modelo1">Modelo 1</option>
                <option value="modelo2">Modelo 2</option>
                <option value="modelo3">Modelo 3</option>
                <option value="modelo4">Modelo 4</option>
                <option value="modelo5">Modelo 5</option>
              </select>
            </div>

            <div className="col-12 col-md-auto order-1 order-md-1">
              <div className="d-flex gap-2 flex-wrap justify-content-end justify-content-md-end">
                {aprendiz.testKolbId ? (
                  puedeActualizarTestKolb && (
                    <button
                      className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                      style={{ width: 34, height: 34 }}
                      onClick={() =>
                        navigate(`/aprendiz/testkolb/editar/${aprendiz.testKolbId}`, {
                          state: { aprendiz },
                        })
                      }
                      title="Editar Test de Kolb"
                      type="button"
                      disabled={
                        loading ||
                        deletingId === aprendiz._id ||
                        !puedeActualizarTestKolb
                      }
                    >
                      <i className="fas fa-brain"></i>
                    </button>
                  )
                ) : (
                  puedeCrearTestKolb && (
                    <button
                      className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 34,
                        height: 34,
                        backgroundColor: "#6f42c1",
                        color: "#fff",
                      }}
                      onClick={() =>
                        navigate(`/aprendiz/testkolb/registrar/${aprendiz._id}`, {
                          state: { aprendiz },
                        })
                      }
                      title="Registrar Test de Kolb"
                      type="button"
                      disabled={
                        loading ||
                        deletingId === aprendiz._id ||
                        !puedeCrearTestKolb
                      }
                    >
                      <i className="fas fa-brain"></i>
                    </button>
                  )
                )}

                <button
                  className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() =>
                    descargarDocx(
                      `/api/compromiso/generar-docx/${aprendiz._id}`,
                      `compromiso_${aprendiz.documentoAprendiz}.docx`
                    )
                  }
                  title="Compromiso del Aprendiz"
                  type="button"
                  disabled={loading || deletingId === aprendiz._id}
                >
                  <i className="fa-solid fa-file-word"></i>
                </button>

                <button
                  className="btn btn-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() =>
                    descargarDocx(
                      `/api/tratamiento/generar-docx/${aprendiz._id}`,
                      `tratamiento_${aprendiz.documentoAprendiz}.docx`
                    )
                  }
                  title="Tratamiento de Datos"
                  type="button"
                  disabled={loading || deletingId === aprendiz._id}
                >
                  <i className="fa-solid fa-file-word"></i>
                </button>

                {puedeActualizar && (
                  <button
                    className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() =>
                      navigate(`/aprendiz/edit/${aprendiz._id}`, {
                        state: { aprendiz },
                      })
                    }
                    title="Editar Aprendiz"
                    type="button"
                    disabled={
                      loading ||
                      deletingId === aprendiz._id ||
                      !puedeActualizar
                    }
                  >
                    <i className="fa-solid fa-pen-to-square"></i>
                  </button>
                )}

                {puedeEliminar && (
                  <button
                    className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() => deleteAprendiz(aprendiz._id)}
                    title="Eliminar Aprendiz"
                    type="button"
                    disabled={
                      loading ||
                      deletingId === aprendiz._id ||
                      !puedeEliminar
                    }
                  >
                    {deletingId === aprendiz._id ? (
                      <i className="fa-solid fa-spinner fa-spin text-white"></i>
                    ) : (
                      <i className="fa-solid fa-trash"></i>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        ),
      })),
    [
      aprendices,
      loading,
      deletingId,
      puedeActualizar,
      puedeEliminar,
      puedeCrearTestKolb,
      puedeActualizarTestKolb,
      navigate,
      descargarDocx,
      deleteAprendiz,
    ]
  );

  const columns = useMemo(
    () => [
      { accessor: "imagen" },
      { Header: "Documento", accessor: "documentoAprendiz" },
      { Header: "Nombre", accessor: "nombre1Aprendiz" },
      { Header: "Apellido", accessor: "apellido1Aprendiz" },
      { Header: "Edad", accessor: "edadAprendiz" },
      { Header: "Sexo", accessor: "sexoAprendiz" },
      {
        Header: "Email",
        accessor: "emailAprendiz",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Aprendices</h5>
                <small className="text-secondary">
                  Gestión institucional y generación de documentos.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() => navigate("/aprendiz/nuevo")}
                    title="Crear Aprendiz"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando aprendices…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">👨‍🎓</div>

                <div className="fw-semibold">
                  No hay aprendices registrados
                </div>

                <div className="small mb-3">
                  Crea el primer registro para iniciar la gestión.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate("/aprendiz/registraraprendices")}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar aprendiz
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarAprendices;